// src/app/core/services/storage.service.ts

import { Injectable, inject } from '@angular/core';

import { environment } from '../../../environments/environment';

import { EncryptionService } from './encryption-service';

export type StorageType = 'local' | 'session';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private readonly encryptionService = inject(EncryptionService);

  private readonly encryptionEnabled = environment.encrypt;

  setLocal<T>(key: string, value: T): void {
    this.setItem('local', key, value);
  }

  getLocal<T>(key: string): T | null {
    return this.getItem<T>('local', key);
  }

  removeLocal(key: string): void {
    this.removeItem('local', key);
  }

  clearLocal(): void {
    localStorage.clear();
  }

  setSession<T>(key: string, value: T): void {
    this.setItem('session', key, value);
  }

  getSession<T>(key: string): T | null {
    return this.getItem<T>('session', key);
  }

  removeSession(key: string): void {
    this.removeItem('session', key);
  }

  clearSession(): void {
    sessionStorage.clear();
  }

  private setItem<T>(storageType: StorageType, key: string, value: T): void {
    const storage = this.getStorage(storageType);

    const rawValue = JSON.stringify(value);

    if (!this.encryptionEnabled) {
      storage.setItem(key, rawValue);
      return;
    }

    const encryptedKey = this.encryptionService.encryptDeterministic(key);

    const encryptedValue = this.encryptionService.encrypt(rawValue);

    storage.setItem(encryptedKey, encryptedValue);
  }

  private getItem<T>(storageType: StorageType, key: string): T | null {
    const storage = this.getStorage(storageType);

    const finalKey = this.encryptionEnabled
      ? this.encryptionService.encryptDeterministic(key)
      : key;

    const storedValue = storage.getItem(finalKey);

    if (!storedValue) {
      return null;
    }

    try {
      const rawValue = this.encryptionEnabled
        ? this.encryptionService.decrypt(storedValue)
        : storedValue;

      return JSON.parse(rawValue) as T;
    } catch {
      return null;
    }
  }

  private removeItem(storageType: StorageType, key: string): void {
    const storage = this.getStorage(storageType);

    const finalKey = this.encryptionEnabled
      ? this.encryptionService.encryptDeterministic(key)
      : key;

    storage.removeItem(finalKey);
  }

  private getStorage(storageType: StorageType): Storage {
    return storageType === 'local' ? localStorage : sessionStorage;
  }
}
